import DeleteIcon from '@mui/icons-material/Delete';
import {
    Alert,
    Box,
    Button,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CassaDiffEntry } from '../domain/cashDrawer/cassaDiffReg';
import useRequest from '../hooks/useRequest';

const today = new Date().toISOString().slice(0, 10);

const CassaDiffRegistry = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [entries, setEntries] = useState<CassaDiffEntry[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [formValue, setFormValue] = useState('');
    const [formObs, setFormObs] = useState('');

    // Filter state
    const [filterStartDate, setFilterStartDate] = useState(today.slice(0, 7) + '-01');
    const [filterEndDate, setFilterEndDate] = useState(today);

    const load = useCallback(async () => {
        try {
            const params: Record<string, unknown> = {};
            if (filterStartDate) params.startDate = filterStartDate;
            if (filterEndDate) params.endDate = filterEndDate;

            const data = await request<CassaDiffEntry[]>({
                path: '/cassa-diff',
                params,
            });
            setEntries(data);
        } catch {
            setError(t('cashDrawer.cassaDiff.loadError'));
        }
    }, [request, t, filterStartDate, filterEndDate]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleAdd = async () => {
        const parsedValue = parseFloat(formValue);
        if (isNaN(parsedValue)) return;

        try {
            await request({
                path: '/cassa-diff',
                method: 'POST',
                params: {
                    value: parsedValue,
                    obs: formObs.trim() || null,
                },
            });
            setFormValue('');
            setFormObs('');
            await load();
        } catch {
            setError(t('cashDrawer.cassaDiff.saveError'));
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await request({ path: `/cassa-diff/${id}`, method: 'DELETE' });
            await load();
        } catch {
            setError(t('cashDrawer.cassaDiff.deleteError'));
        }
    };

    const totalValue = entries.reduce((sum, entry) => sum + (entry.value ?? 0), 0);

    let totalValueColor = 'text.primary';
    if (totalValue > 0) {
        totalValueColor = 'success.main';
    } else if (totalValue < 0) {
        totalValueColor = 'error.main';
    }

    return (
        <Paper sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
                {t('cashDrawer.cassaDiff.title')}
            </Typography>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Add form */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
                    <TextField
                        size="small"
                        type="number"
                        label={t('cashDrawer.cassaDiff.value')}
                        value={formValue}
                        onChange={event => setFormValue(event.target.value)}
                        sx={{ width: 140 }}
                        slotProps={{ htmlInput: { step: '0.01' } }}
                    />
                    <TextField
                        size="small"
                        label={t('cashDrawer.cassaDiff.observations')}
                        value={formObs}
                        onChange={event => setFormObs(event.target.value)}
                        sx={{ minWidth: 220, flexGrow: 1 }}
                    />
                    <Button variant="contained" onClick={handleAdd} disabled={!formValue}>
                        {t('common.add')}
                    </Button>
                </Stack>
            </Paper>

            {/* Filters */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <TextField
                    size="small"
                    type="date"
                    label={t('cashDrawer.cassaDiff.startDate')}
                    value={filterStartDate}
                    onChange={event => setFilterStartDate(event.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                    size="small"
                    type="date"
                    label={t('cashDrawer.cassaDiff.endDate')}
                    value={filterEndDate}
                    onChange={event => setFilterEndDate(event.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <Box flexGrow={1} />
                <Typography variant="subtitle1" fontWeight="bold">
                    {t('cashDrawer.cassaDiff.total')}:
                </Typography>
                <Typography variant="h6" color={totalValueColor}>
                    {totalValue.toFixed(0)} RON
                </Typography>
            </Stack>

            {/* Table */}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('cashDrawer.cassaDiff.dateTime')}</TableCell>
                            <TableCell>{t('cashDrawer.cassaDiff.user')}</TableCell>
                            <TableCell align="right">{t('cashDrawer.cassaDiff.value')}</TableCell>
                            <TableCell>{t('cashDrawer.cassaDiff.observations')}</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Box py={2} color="text.secondary">
                                        {t('cashDrawer.cassaDiff.noData')}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        {entries.map(entry => (
                            <TableRow key={entry.id}>
                                <TableCell>
                                    {entry.stamp ? new Date(entry.stamp).toLocaleString() : '-'}
                                </TableCell>
                                <TableCell>{entry.username ?? '-'}</TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color:
                                            (entry.value ?? 0) > 0 ? 'success.main' : 'error.main',
                                        fontWeight: 600,
                                    }}
                                >
                                    {(entry.value ?? 0).toFixed(0)}
                                </TableCell>
                                <TableCell>{entry.obs ?? '-'}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title={t('common.delete')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(entry.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default CassaDiffRegistry;
